import { useEffect, useRef, useState } from 'react';
import { D } from '@makila/fiscal';
import { Camera, Plus, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { mzDateTime, printHtml } from '../lib/receipt';
import { guideHtml, type Guide } from '../lib/guide';
import { api, post } from './api';
import { StockRequests } from './FashionPages';
import { ManagedByMakila } from './makila';
import { Alert, Button, Card, Field, Input, Select } from './ui';
import { attempt, useLoad } from './useLoad';

interface Store { id: string; code: string; name: string }
interface Product { id: string; code: string; name: string; barcode: string | null; unit: string; active: boolean; track_stock: boolean; track_lots?: boolean }
interface Level { id: string; code: string; name: string; unit: string; quantity: string; min_stock: string | null; below: boolean }
interface Transfer { id: string; number: number; status: 'sent' | 'received' | 'cancelled'; from_name: string; to_name: string; to_store: string; sent_at: string; lines: { name: string; quantity: string }[] }
interface Count { id: string; started_at: string; lines: { product_id: string; code: string; name: string; unit: string; counted: string; current: string }[] }

const TABS = [['levels', 'Saldos'], ['receipt', 'Entrada'], ['transfers', 'Transferências'], ['requests', 'Pedidos'], ['count', 'Inventário'], ['expiry', 'Validades e abates']] as const;
const q3 = (v: string) => new D(v).toString().replace('.', ',');
const parseQty = (v: string) => { const s = v.trim().replace(',', '.'); return /^\d{1,12}(\.\d{1,3})?$/.test(s) ? s : null; };

/** Stock por loja: saldos, entradas de mercadoria, transferências entre lojas e inventário (também no telemóvel). */
export function StockPage() {
  const stores = useLoad<{ items: Store[] }>('/stores');
  const products = useLoad<{ items: Product[] }>('/products');
  const [storeId, setStoreId] = useState<string | null>(null);
  const [tab, setTab] = useState<(typeof TABS)[number][0]>('levels');
  useEffect(() => { if (!storeId && stores.data?.items[0]) setStoreId(stores.data.items[0].id); }, [stores.data, storeId]);
  if (stores.error) return <Alert>{stores.error}</Alert>;
  if (!stores.data || !products.data || !storeId) return <p className="text-muted">A carregar…</p>;
  const tracked = products.data.items.filter((p) => p.active && p.track_stock);
  return (
    <div className="max-w-5xl space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        {stores.data.items.length > 1 && (
          <Field label="Loja"><Select value={storeId} onChange={(e) => setStoreId(e.target.value)}>{stores.data.items.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}</Select></Field>
        )}
        <div role="tablist" className="flex flex-wrap gap-1">
          {TABS.map(([k, label]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={clsx('h-11 rounded-lg px-4 font-medium', tab === k ? 'bg-brand-700 text-white' : 'border border-base')}>{label}</button>
          ))}
        </div>
      </div>
      {tab === 'levels' && <Levels key={storeId} storeId={storeId} />}
      {tab === 'receipt' && <Receipt key={storeId} storeId={storeId} products={tracked} onDone={() => setTab('levels')} />}
      {tab === 'transfers' && <Transfers key={storeId} storeId={storeId} stores={stores.data.items} products={tracked} />}
      {tab === 'requests' && <StockRequests />}
      {tab === 'count' && <Inventory key={storeId} storeId={storeId} products={tracked} />}
      {tab === 'expiry' && <Expiry key={storeId} storeId={storeId} products={tracked} />}
    </div>
  );
}

function Levels({ storeId }: { storeId: string }) {
  const { data, error } = useLoad<{ items: Level[]; belowMin: number; managedByMakila: boolean }>(`/stock?storeId=${storeId}`);
  const [q, setQ] = useState('');
  const [onlyLow, setOnlyLow] = useState(false);
  const [hist, setHist] = useState<{ p: Level; items: { kind: string; quantity: string; note: string | null; created_at: string }[] } | null>(null);
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <p className="text-muted">A carregar…</p>;
  const list = data.items.filter((i) => (!onlyLow || i.below) && (!q || `${i.name} ${i.code}`.toLowerCase().includes(q.toLowerCase())));
  const KIND: Record<string, string> = { sale: 'Venda', return: 'Devolução', receipt: 'Entrada', adjust: 'Inventário', transfer_out: 'Transferência (saída)', transfer_in: 'Transferência (entrada)' };
  return (
    <>
      {data.managedByMakila && <ManagedByMakila what="saldos de stock" />}
      {data.belowMin > 0 && <Alert>{data.belowMin} artigo(s) abaixo do stock mínimo. <button className="font-semibold underline" onClick={() => setOnlyLow(!onlyLow)}>{onlyLow ? 'Ver todos' : 'Ver só esses'}</button></Alert>}
      {hist && (
        <Card title={`Movimentos — ${hist.p.name}`} actions={<Button variant="ghost" onClick={() => setHist(null)}>Fechar</Button>}>
          <ul className="divide-y divide-[var(--border)] text-sm">
            {hist.items.map((m, i) => (
              <li key={i} className="flex gap-3 py-1.5"><span className="text-muted">{mzDateTime(m.created_at)}</span><span className="flex-1">{KIND[m.kind] ?? m.kind}{m.note ? ` · ${m.note}` : ''}</span><span className={clsx('num', new D(m.quantity).isNegative() && 'text-brand-700')}>{q3(m.quantity)}</span></li>
            ))}
            {!hist.items.length && <li className="py-2 text-muted">Sem movimentos nesta loja.</li>}
          </ul>
        </Card>
      )}
      <Card title={`${data.items.length} artigos com stock`} actions={<Input aria-label="Pesquisar no stock" placeholder="Pesquisar" value={q} onChange={(e) => setQ(e.target.value)} className="w-48" />}>
        <table className="w-full text-sm">
          <thead className="text-left text-muted"><tr><th className="py-2">Artigo</th><th className="text-right">Mínimo</th><th className="text-right">Saldo</th></tr></thead>
          <tbody className="divide-y divide-[var(--border)]">
            {list.map((i) => (
              <tr key={i.id} className="cursor-pointer hover:bg-surface-2" onClick={async () => setHist({ p: i, items: (await api<{ items: never[] }>(`/stock/movements?storeId=${storeId}&productId=${i.id}`)).items })}>
                <td className="py-2"><span className="font-mono text-xs text-muted">{i.code}</span> {i.name}</td>
                <td className="num text-right text-muted">{i.min_stock ? q3(i.min_stock) : '—'}</td>
                <td className={clsx('num text-right font-semibold', (i.below || new D(i.quantity).isNegative()) && 'text-brand-700')}>{q3(i.quantity)} <small className="font-normal text-muted">{i.unit}</small></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

/** Escolher um artigo: pesquisa pelo nome, código ou código de barras (leitor ou câmara). */
function ProductPicker({ products, onPick, label = 'Artigo' }: { products: Product[]; onPick: (p: Product) => void; label?: string }) {
  const [q, setQ] = useState('');
  const [cam, setCam] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const s = q.trim().toLowerCase();
  const hits = s ? products.filter((p) => p.barcode === q.trim() || p.code.toLowerCase() === s || p.name.toLowerCase().includes(s)).slice(0, 8) : [];
  const take = (p: Product) => { onPick(p); setQ(''); setErr(null); };
  const byCode = (code: string) => {
    const p = products.find((x) => x.barcode === code || x.code.toLowerCase() === code.toLowerCase());
    if (p) take(p); else setErr(`Nenhum artigo com o código ${code}`);
  };
  return (
    <div>
      <div className="flex gap-2">
        <Field label={label}>
          <Input aria-label={`Procurar ${label.toLowerCase()}`} placeholder="Nome, código ou ler o código de barras" value={q} onChange={(e) => { setQ(e.target.value); setErr(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const exact = products.find((x) => x.barcode === q.trim() || x.code.toLowerCase() === s); if (exact) take(exact); else if (hits.length === 1) take(hits[0]!); else if (s) setErr(`Nenhum artigo com o código ${q.trim()}`); } }} />
        </Field>
        {'BarcodeDetector' in window && <Button variant="ghost" className="mt-6" aria-label="Ler com a câmara" onClick={() => setCam(!cam)}><Camera size={18} /></Button>}
      </div>
      {cam && <CameraScanner onCode={(c) => { setCam(false); byCode(c); }} />}
      {err && <p className="mt-1 text-sm text-brand-700">{err}</p>}
      {hits.length > 0 && (
        <ul className="mt-1 divide-y divide-[var(--border)] rounded-lg border border-base">
          {hits.map((p) => <li key={p.id}><button className="flex h-11 w-full items-center gap-2 px-3 text-left hover:bg-surface-2" onClick={() => take(p)}><span className="font-mono text-xs text-muted">{p.code}</span> {p.name}</button></li>)}
        </ul>
      )}
    </div>
  );
}

/** Leitura pela câmara do telemóvel (BarcodeDetector do navegador: Chrome no Android). */
function CameraScanner({ onCode }: { onCode: (code: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let stream: MediaStream | null = null;
    let stop = false;
    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (!video.current) return;
        video.current.srcObject = stream;
        await video.current.play();
        const Detector = (window as unknown as { BarcodeDetector: new (o: { formats: string[] }) => { detect: (v: HTMLVideoElement) => Promise<{ rawValue: string }[]> } }).BarcodeDetector;
        const det = new Detector({ formats: ['ean_13', 'ean_8', 'code_128', 'upc_a'] });
        while (!stop) {
          const found = await det.detect(video.current).catch(() => []);
          if (found[0]) { onCode(found[0].rawValue); return; }
          await new Promise((r) => setTimeout(r, 250));
        }
      } catch { setErr('Não foi possível usar a câmara. Escreva o código ou use o leitor.'); }
    })();
    return () => { stop = true; stream?.getTracks().forEach((t) => t.stop()); };
  }, [onCode]);
  return err ? <p className="mt-2 text-sm text-brand-700">{err}</p> : <video ref={video} muted playsInline className="mt-2 max-h-64 w-full rounded-lg bg-black object-cover" />;
}

type Row = { p: Product; qty: string; cost?: string; lot?: string; expiry?: string };

function Lines({ rows, set, cost }: { rows: Row[]; set: (r: Row[]) => void; cost?: boolean }) {
  return (
    <ul className="mt-3 divide-y divide-[var(--border)]">
      {rows.map((r, i) => (
        <li key={`${r.p.id}-${i}`} className="flex flex-wrap items-center gap-2 py-2">
          <span className="min-w-0 flex-1">{r.p.name}</span>
          <Input aria-label={`Quantidade de ${r.p.name}`} inputMode="decimal" className="w-24" value={r.qty} onChange={(e) => set(rows.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} />
          <span className="text-sm text-muted">{r.p.unit}</span>
          {cost && r.p.track_lots && <>
            <Input aria-label={`Lote de ${r.p.name}`} placeholder="Lote" className="w-28" value={r.lot ?? ''} onChange={(e) => set(rows.map((x, j) => (j === i ? { ...x, lot: e.target.value } : x)))} />
            <Input aria-label={`Validade de ${r.p.name}`} type="date" className="w-40" value={r.expiry ?? ''} onChange={(e) => set(rows.map((x, j) => (j === i ? { ...x, expiry: e.target.value } : x)))} />
          </>}
          {cost && <Input aria-label={`Custo de ${r.p.name}`} inputMode="decimal" placeholder="Custo (opcional)" className="w-36" value={r.cost ?? ''} onChange={(e) => set(rows.map((x, j) => (j === i ? { ...x, cost: e.target.value } : x)))} />}
          <button aria-label={`Retirar ${r.p.name}`} onClick={() => set(rows.filter((_, j) => j !== i))} className="grid h-10 w-10 place-items-center rounded-lg text-brand-700"><Trash2 size={16} /></button>
        </li>
      ))}
    </ul>
  );
}
// Artigos com lotes podem repetir-se (um lote por linha).
const addRow = (rows: Row[], p: Product): Row[] => (rows.some((r) => r.p.id === p.id) && !p.track_lots ? rows : [...rows, { p, qty: '1' }]);
const linesBody = (rows: Row[]) => {
  const bad = rows.find((r) => !parseQty(r.qty) || new D(parseQty(r.qty)!).isZero());
  if (bad) throw new Error(`Quantidade inválida em ${bad.p.name}`);
  return rows.map((r) => ({ productId: r.p.id, quantity: parseQty(r.qty)!, ...(r.cost?.trim() ? { unitCost: r.cost.trim().replace(',', '.') } : {}), ...(r.lot?.trim() ? { lot: r.lot.trim(), expiry: r.expiry } : {}) }));
};

function Receipt({ storeId, products, onDone }: { storeId: string; products: Product[]; onDone: () => void }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [f, setF] = useState({ supplier: '', reference: '' });
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    let lines;
    try { lines = linesBody(rows); } catch (e) { setErr((e as Error).message); return; }
    const e = await attempt(() => post('/stock/receipts', { storeId, supplier: f.supplier || undefined, reference: f.reference || undefined, lines }));
    setErr(e);
    if (!e) onDone();
  };
  return (
    <Card title="Entrada de mercadoria">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Fornecedor"><Input value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} /></Field>
        <Field label="N.º da factura ou guia do fornecedor"><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} /></Field>
      </div>
      <div className="mt-3"><ProductPicker products={products} onPick={(p) => setRows(addRow(rows, p))} /></div>
      <Lines rows={rows} set={setRows} cost />
      {err && <div className="mt-3"><Alert>{err}</Alert></div>}
      <Button className="mt-3" disabled={!rows.length} onClick={save}>Dar entrada</Button>
    </Card>
  );
}

function Transfers({ storeId, stores, products }: { storeId: string; stores: Store[]; products: Product[] }) {
  const list = useLoad<{ items: Transfer[] }>('/stock/transfers');
  const others = stores.filter((s) => s.id !== storeId);
  const [to, setTo] = useState(others[0]?.id ?? '');
  const [vehicle, setVehicle] = useState('');
  const printGuide = async (id: string) => { try { void printHtml(guideHtml((await api<{ guide: Guide }>(`/stock/transfers/${id}/guide`)).guide)); } catch (e) { setErr((e as Error).message); } };
  const [rows, setRows] = useState<Row[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    let lines;
    try { lines = linesBody(rows); } catch (e) { setErr((e as Error).message); return; }
    let created: { id: string } | null = null;
    const e = await attempt(async () => { created = await post<{ id: string }>('/stock/transfers', { fromStore: storeId, toStore: to, lines, vehicle: vehicle.trim() || undefined }); });
    setErr(e);
    if (!e) { setRows([]); setVehicle(''); void list.reload(); if (created) void printGuide((created as { id: string }).id); }
  };
  const STATUS = { sent: 'A caminho', received: 'Recebida', cancelled: 'Anulada' };
  return (
    <>
      {!others.length ? <Alert tone="info">As transferências são entre lojas. Crie outra loja em "Lojas e caixas".</Alert> : (
        <Card title="Enviar para outra loja">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Para"><Select value={to} onChange={(e) => setTo(e.target.value)}>{others.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}</Select></Field>
            <Field label="Matrícula da viatura (guia de remessa)"><Input value={vehicle} onChange={(e) => setVehicle(e.target.value.toUpperCase())} placeholder="AAA-123-MC" /></Field>
          </div>
          <div className="mt-3"><ProductPicker products={products} onPick={(p) => setRows(addRow(rows, p))} /></div>
          <Lines rows={rows} set={setRows} />
          {err && <div className="mt-3"><Alert>{err}</Alert></div>}
          <Button className="mt-3" disabled={!rows.length || !to} onClick={send}>Enviar e imprimir a guia de remessa</Button>
        </Card>
      )}
      <Card title="Transferências">
        {list.data && !list.data.items.length && <p className="text-muted">Ainda não há transferências.</p>}
        <ul className="divide-y divide-[var(--border)]">
          {list.data?.items.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold">N.º {t.number} · {t.from_name} → {t.to_name} <span className="ml-1 font-normal text-muted">{STATUS[t.status]}</span></div>
                <div className="text-sm text-muted">{mzDateTime(t.sent_at)} · {t.lines.map((l) => `${q3(l.quantity)} ${l.name}`).join(', ')}</div>
              </div>
              <Button variant="ghost" onClick={() => void printGuide(t.id)}>Guia</Button>
              {t.status === 'sent' && <>
                {t.to_store === storeId && <Button onClick={async () => { setErr(await attempt(() => post(`/stock/transfers/${t.id}/receive`))); void list.reload(); }}>Recebida</Button>}
                <Button variant="ghost" onClick={async () => { setErr(await attempt(() => post(`/stock/transfers/${t.id}/cancel`))); void list.reload(); }}>Anular</Button>
              </>}
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

function Inventory({ storeId, products }: { storeId: string; products: Product[] }) {
  const open = useLoad<{ count: Count | null }>(`/stock/counts/open?storeId=${storeId}`);
  const [cur, setCur] = useState<{ p: Product; qty: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ productId: string; counted: string; expected: string; difference: string }[] | null>(null);
  if (open.error) return <Alert>{open.error}</Alert>;
  if (!open.data) return <p className="text-muted">A carregar…</p>;
  const c = open.data.count;
  const name = new Map(products.map((p) => [p.id, p.name]));
  if (done) {
    const diff = done.filter((x) => !new D(x.difference).isZero());
    return (
      <Card title="Inventário fechado">
        <p>{done.length} artigo(s) contados; {diff.length} com diferença, já ajustados.</p>
        <ul className="mt-2 text-sm">{diff.map((x) => <li key={x.productId}>{name.get(x.productId)}: tinha {q3(x.expected)}, contados {q3(x.counted)} ({new D(x.difference).isPositive() ? '+' : ''}{q3(x.difference)})</li>)}</ul>
        <Button className="mt-3" variant="ghost" onClick={() => { setDone(null); void open.reload(); }}>OK</Button>
      </Card>
    );
  }
  if (!c) {
    return (
      <Card title="Inventário">
        <p className="text-muted">Conte a loja (toda ou só parte): leia ou procure cada artigo e escreva quantos há. No fecho, cada artigo contado fica com o que se contou; os não contados não mexem.</p>
        {err && <div className="mt-3"><Alert>{err}</Alert></div>}
        <Button className="mt-3" onClick={async () => { setErr(await attempt(() => post('/stock/counts', { storeId }))); void open.reload(); }}><Plus size={16} /> Começar a contar</Button>
      </Card>
    );
  }
  const save = async () => {
    if (!cur) return;
    const v = parseQty(cur.qty);
    if (!v) { setErr('Quantidade inválida'); return; }
    const e = await attempt(() => api(`/stock/counts/${c.id}/lines/${cur.p.id}`, { method: 'PUT', body: JSON.stringify({ counted: v }) }));
    setErr(e);
    if (!e) { setCur(null); void open.reload(); }
  };
  return (
    <>
      <Card title={`Contagem desde ${mzDateTime(c.started_at)}`}>
        {!cur ? <ProductPicker products={products} label="Artigo a contar" onPick={(p) => setCur({ p, qty: c.lines.find((l) => l.product_id === p.id)?.counted ? q3(c.lines.find((l) => l.product_id === p.id)!.counted) : '' })} /> : (
          <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); void save(); }}>
            <Field label={`Quantos ${cur.p.name}?`}><Input autoFocus aria-label="Quantidade contada" inputMode="decimal" value={cur.qty} onChange={(e) => setCur({ ...cur, qty: e.target.value })} className="w-32" /></Field>
            <Button>Gravar</Button><Button type="button" variant="ghost" onClick={() => setCur(null)}>Cancelar</Button>
          </form>
        )}
        {err && <div className="mt-3"><Alert>{err}</Alert></div>}
      </Card>
      <Card title={`${c.lines.length} contado(s)`} actions={<div className="flex gap-2">
        <Button disabled={!c.lines.length} onClick={async () => {
          try { setDone((await post<{ adjustments: NonNullable<typeof done> }>(`/stock/counts/${c.id}/close`)).adjustments); } catch (e) { setErr((e as Error).message); }
        }}>Fechar e ajustar</Button>
        <Button variant="ghost" onClick={async () => { setErr(await attempt(() => post(`/stock/counts/${c.id}/cancel`))); void open.reload(); }}>Anular</Button>
      </div>}>
        <table className="w-full text-sm">
          <thead className="text-left text-muted"><tr><th className="py-2">Artigo</th><th className="text-right">Saldo agora</th><th className="text-right">Contado</th></tr></thead>
          <tbody className="divide-y divide-[var(--border)]">
            {c.lines.map((l) => (
              <tr key={l.product_id}>
                <td className="py-2">{l.name}</td>
                <td className="num text-right text-muted">{q3(l.current)}</td>
                <td className={clsx('num text-right font-semibold', !new D(l.counted).equals(l.current) && 'text-brand-700')}>{q3(l.counted)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

interface ExpiryRow { lot_id: string; number: string; expiry: string; product_id: string; code: string; name: string; quantity: string; band: 'expired' | 'recalled' | 30 | 60 | 90 }
const BAND: Record<string, string> = { expired: 'Vencido', recalled: 'Recolhido', 30: 'Até 30 dias', 60: 'Até 60 dias', 90: 'Até 90 dias' };

/** Farmácia: o que vence (art. 65), recolhas de lote (ANARME) com rastreio, e abates/devoluções ao fornecedor (art. 48). */
function Expiry({ storeId, products }: { storeId: string; products: Product[] }) {
  const list = useLoad<{ items: ExpiryRow[] }>(`/stock/expiry?storeId=${storeId}`);
  const [trace, setTrace] = useState<{ lot: { number: string; name: string; recall_reason: string | null }; stock: { store: string; quantity: string }[]; sold: { full_number: string; issue_date: string; customer_name: string | null; quantity: string }[] } | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [w, setW] = useState({ kind: 'writeoff' as 'writeoff' | 'supplier_return', reason: '', supplier: '' });
  const [err, setErr] = useState<string | null>(null);
  const [recallFor, setRecallFor] = useState<{ r: ExpiryRow; reason: string } | null>(null);
  if (list.error) return <Alert>{list.error}</Alert>;
  if (!list.data) return <p className="text-muted">A carregar…</p>;
  const recall = async () => {
    if (!recallFor || recallFor.reason.trim().length < 3) return;
    setErr(await attempt(() => post(`/stock/lots/${recallFor.r.lot_id}/recall`, { reason: recallFor.reason.trim() })));
    setRecallFor(null);
    void list.reload();
  };
  /** Abater o lote todo (vencido ou recolhido): a saída normal (FEFO) nunca lhe toca. */
  const writeoffLot = async (r: ExpiryRow) => {
    setErr(await attempt(() => post('/stock/writeoffs', { storeId, kind: 'writeoff', reason: r.band === 'recalled' ? 'Lote recolhido' : 'Vencido', lines: [{ productId: r.product_id, lotId: r.lot_id, quantity: r.quantity }] })));
    void list.reload();
  };
  const writeoff = async () => {
    let lines;
    try { lines = linesBody(rows).map((l) => ({ productId: l.productId, quantity: l.quantity })); } catch (e) { setErr((e as Error).message); return; }
    const e = await attempt(() => post('/stock/writeoffs', { storeId, kind: w.kind, reason: w.reason, supplier: w.supplier || undefined, lines }));
    setErr(e);
    if (!e) { setRows([]); setW({ ...w, reason: '' }); void list.reload(); }
  };
  return (
    <>
      {err && <Alert>{err}</Alert>}
      <Card title="Validades e recolhas">
        {!list.data.items.length && <p className="text-muted">Nada vencido nem a vencer nos próximos 90 dias.</p>}
        <table className="w-full text-sm">
          <tbody className="divide-y divide-[var(--border)]">
            {list.data.items.map((r) => (
              <tr key={r.lot_id}>
                <td className="py-2">{r.name}<span className="block text-xs text-muted">lote {r.number} · validade {r.expiry.split('-').reverse().join('/')}</span></td>
                <td><span className={clsx('rounded px-2 py-0.5 text-xs font-semibold', r.band === 'expired' || r.band === 'recalled' ? 'bg-brand-700 text-white' : r.band === 30 ? 'bg-amber-400 text-black' : 'bg-surface-2')}>{BAND[String(r.band)]}</span></td>
                <td className="num text-right">{q3(r.quantity)}</td>
                <td className="whitespace-nowrap text-right">
                  <Button variant="ghost" onClick={async () => setTrace(await api(`/stock/lots/${r.lot_id}/trace`))}>Rastrear</Button>
                  {r.band !== 'recalled' && <Button variant="ghost" className="ml-1" onClick={() => setRecallFor({ r, reason: '' })}>Recolher</Button>}
                  {(r.band === 'expired' || r.band === 'recalled') && <Button variant="danger" className="ml-1" onClick={() => void writeoffLot(r)}>Abater</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {recallFor && (
        <Card title={`Recolher o lote ${recallFor.r.number} — ${recallFor.r.name}`}>
          <p className="mb-2 text-sm text-muted">O lote deixa de se vender em todas as caixas (na sincronização seguinte). Use "Rastrear" para saber onde está e a quem se vendeu.</p>
          <div className="flex flex-wrap gap-2">
            <Input aria-label="Motivo da recolha" placeholder="Ex.: circular da ANARME n.º …" value={recallFor.reason} onChange={(e) => setRecallFor({ ...recallFor, reason: e.target.value })} className="flex-1" />
            <Button variant="danger" disabled={recallFor.reason.trim().length < 3} onClick={() => void recall()}>Recolher</Button>
            <Button variant="ghost" onClick={() => setRecallFor(null)}>Cancelar</Button>
          </div>
        </Card>
      )}
      {trace && (
        <Card title={`Lote ${trace.lot.number} — ${trace.lot.name}`} actions={<Button variant="ghost" onClick={() => setTrace(null)}>Fechar</Button>}>
          {trace.lot.recall_reason && <Alert>Recolhido: {trace.lot.recall_reason}</Alert>}
          <div className="mt-2 text-sm font-medium">Onde está</div>
          <ul className="text-sm">{trace.stock.map((s) => <li key={s.store}>{s.store}: {q3(s.quantity)}</li>)}</ul>
          <div className="mt-2 text-sm font-medium">A quem se vendeu</div>
          <ul className="text-sm">{trace.sold.map((s, i) => <li key={i}>{s.issue_date.split('-').reverse().join('/')} · {s.full_number} · {s.customer_name ?? 'Consumidor Final'} · {q3(s.quantity)}</li>)}</ul>
        </Card>
      )}
      <Card title="Abate ou devolução ao fornecedor">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="O quê"><Select value={w.kind} onChange={(e) => setW({ ...w, kind: e.target.value as typeof w.kind })}><option value="writeoff">Abate (vencido, partido…)</option><option value="supplier_return">Devolução ao fornecedor</option></Select></Field>
          <Field label="Motivo"><Input value={w.reason} onChange={(e) => setW({ ...w, reason: e.target.value })} placeholder="Vencido" /></Field>
          {w.kind === 'supplier_return' && <Field label="Fornecedor"><Input value={w.supplier} onChange={(e) => setW({ ...w, supplier: e.target.value })} /></Field>}
        </div>
        <div className="mt-3"><ProductPicker products={products} onPick={(p) => setRows(addRow(rows, p))} /></div>
        <Lines rows={rows} set={setRows} />
        <p className="mt-1 text-xs text-muted">Nos artigos com lotes, sai primeiro o que vence primeiro (os vencidos e recolhidos abatem-se a partir de "Validades").</p>
        <Button className="mt-3" disabled={!rows.length || w.reason.trim().length < 3} onClick={writeoff}>Registar</Button>
      </Card>
    </>
  );
}
